/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

import { Logger } from '../logger';
import { ACCESSORS, createCollectContext } from './accessors';
import { applyTransforms, coerce } from './pipeline';

import { MAIN_USER_ID_KEY, PUBLISHER_COHORTS_KEY } from '../types/collection';

import type { CollectionResult, Dcfg } from '../types/collection';

const logger = Logger.getInstance();

/**
 * Executes the server-delivered data-point collection config
 * (`PropertyConfig.dcfg`). Reads are per-payload: collect() performs fresh
 * reads on every call (auction N+1 picks up values auction N did not have),
 * with expensive shared scans (JSON-LD, dataLayer resolution) cached within a
 * single call. A point that is missing, malformed, or throws is skipped in
 * isolation — missing is never an error and never affects other points or the
 * auction.
 */
class DataCollector {
  private static instance: DataCollector | null = null;
  private config: Dcfg | null = null;
  private lastMainUserId: string | undefined = undefined;

  private constructor() {}

  public static getInstance(): DataCollector {
    if (!DataCollector.instance) {
      DataCollector.instance = new DataCollector();
    }
    return DataCollector.instance;
  }

  public setConfig(dcfg?: Dcfg): void {
    if (dcfg?.v === 1 && Array.isArray(dcfg.points)) {
      this.config = dcfg;
    } else {
      if (dcfg !== undefined && dcfg !== null) {
        logger.debug('Ignoring malformed data collection config.');
      }
      this.config = null;
    }
  }

  public collect(): CollectionResult {
    const result: CollectionResult = { xd: {} };
    if (!this.config || this.config.points.length === 0) {
      return result;
    }

    const ctx = createCollectContext();
    for (const point of this.config.points) {
      try {
        if (typeof point?.key !== 'string' || point.key.length === 0) {
          continue;
        }
        const accessor = ACCESSORS[point.source?.type];
        if (!accessor) {
          logger.debug(`Unknown data collection accessor: ${point.source?.type}`);
          continue;
        }
        const rawValue = accessor(point.source, ctx);
        if (rawValue === undefined || rawValue === null) {
          continue;
        }
        const value = coerce(applyTransforms(rawValue, point.transforms), point.type ?? point.data_type);
        if (value === undefined) {
          continue;
        }
        if (point.key === MAIN_USER_ID_KEY) {
          // The point keyed `uid` is the property's main user id: ships as the
          // single top-level `uid`, never in `xd`. The server enforces at most
          // one; if config declares more than one, the last resolved wins —
          // routing is server-authoritative regardless.
          result.mainUserId = String(value);
          this.lastMainUserId = result.mainUserId;
        } else if (point.key === PUBLISHER_COHORTS_KEY) {
          // The point keyed `pcids` populates the top-level pcids (publisher
          // cohorts / user segments), overriding the vendor-derived value. Only
          // an array routes; a non-array pcids point is dropped, never in `xd`.
          if (Array.isArray(value)) {
            result.pcids = value;
          } else {
            logger.debug("Ignoring non-array value for reserved 'pcids' data point.");
          }
        } else {
          result.xd[point.key] = value;
        }
      } catch (error) {
        logger.debug(`Data collection failed for point '${point?.key}': ${error}`);
      }
    }
    return result;
  }

  /**
   * Last main user id resolved by any collect() call, without performing a
   * fresh collection. For hot per-event paths (impressions, interactions at up
   * to 50 placements) where re-running the dcfg pipeline per event would be
   * too expensive; every such event follows at least one collect() (page view
   * recording or select payload build). Sticky: a later collect() that fails
   * to resolve the uid point does not clear a previously resolved id.
   */
  public getMainUserId(): string | undefined {
    return this.lastMainUserId;
  }

  // This method is used for testing purposes only
  public _reset(): void {
    this.config = null;
    this.lastMainUserId = undefined;
  }
}

export { DataCollector };
